import { bech32, bech32m } from 'bech32';
import bs58check from 'bs58check';

/**
 * decode address with base58 specification, return address version and address hash if valid
 *
 * Backwards-compatible: supports both:
 *  - Bitcoin-style: 1-byte version + 20-byte hash  => payload.length === 21
 *  - Zcash-style:   2-byte version + 20-byte hash  => payload.length === 22
 *
 * Returns:
 *  { version: number, versionBytes: Buffer, hash: Buffer }
 *
 * - `version` is a number:
 *    - for 1-byte version, it's that byte (0..255)
 *    - for 2-byte version, it's the big-endian uint16 (0..65535)
 * - `versionBytes` is the raw Buffer of 1 or 2 bytes
 * - `hash` is the 20-byte payload (as Buffer)
 */
export function fromBase58Check(address: string) {
  const payload = bs58check.decode(address);

  // Accept either 21 or 22 byte payloads (1 or 2 version bytes + 20 byte hash)
  if (payload.length === 21) {
    // bitcoin-style: 1-byte version + 20-byte hash
    const versionBytes = payload.slice(0, 1);
    const version = versionBytes[0];
    const hash = payload.slice(1);
    return { version, versionBytes, hash };
  }

  if (payload.length === 22) {
    // zcash-style: 2-byte version + 20-byte hash (big-endian)
    const versionBytes = payload.slice(0, 2);
    // read as big-endian uint16 - works for both Buffer and Uint8Array
    const version = (versionBytes[0] << 8) | versionBytes[1];
    const hash = payload.slice(2);
    return { version, versionBytes, hash };
  }

  // anything else is unexpected
  if (payload.length < 21) throw new TypeError(address + ' is too short');
  if (payload.length > 22) throw new TypeError(address + ' is too long');
  // Fallback just in case
  throw new TypeError(address + ' has unexpected payload length');
}
/**
 * decode address with bech32 specification,  return address version、address prefix and address data if valid
 */
export function fromBech32(
  address: string,
  mode: 'bitcoin' | 'zcash' = 'bitcoin'
) {
  let result;
  let version;
  let encoding: 'bech32' | 'bech32m';
  try {
    result = bech32.decode(address, 840);
  } catch (_e) {}
  if (result) {
    version = result.words[0];
    encoding = 'bech32';
    if (mode === 'bitcoin' && version !== 0) {
      // Enforce BIP-173 / BIP-350 rules
      throw new TypeError(address + ' uses wrong encoding');
    }
  } else {
    result = bech32m.decode(address, 840);
    version = result.words[0];
    encoding = 'bech32m';
    if (mode === 'bitcoin' && version === 0) {
      // Enforce BIP-173 / BIP-350 rules
      throw new TypeError(address + ' uses wrong encoding');
    }
  }
  return {
    version,
    prefix: result.prefix,
    encoding,
  };
}
